/** Fonte única das rotas da API — evita string solta espalhada pelas features. */
export const ENDPOINTS = {
  auth: {
    login: '/auth/login',
    sessionLogin: '/auth/session-login',
    logout: '/auth/logout',
    me: '/auth/me',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    acceptInvite: '/auth/accept-invite',
  },
  users: {
    root: '/users',
    byId: (id: string) => `/users/${id}`,
    invite: '/users/invite',
    status: (id: string) => `/users/${id}/status`,
  },
  customers: {
    root: '/customers',
    byId: (id: string) => `/customers/${id}`,
  },
  areas: {
    root: '/areas',
    byId: (id: string) => `/areas/${id}`,
    deactivate: (id: string) => `/areas/${id}/deactivate`,
  },
  organizationStructure: {
    units: '/organizational-units',
    unitById: (id: string) => `/organizational-units/${id}`,
    unitDeactivate: (id: string) => `/organizational-units/${id}/deactivate`,
    unitTypes: '/organizational-unit-types',
    positions: '/positions',
    positionById: (id: string) => `/positions/${id}`,
    positionDeactivate: (id: string) => `/positions/${id}/deactivate`,
    departments: '/departments',
    departmentById: (id: string) => `/departments/${id}`,
    departmentDeactivate: (id: string) => `/departments/${id}/deactivate`,
  },
  spaces: {
    root: '/spaces',
    byId: (id: string) => `/spaces/${id}`,
    deactivate: (id: string) => `/spaces/${id}/deactivate`,
  },
  collaborators: {
    root: '/collaborators',
    byId: (id: string) => `/collaborators/${id}`,
    deactivate: (id: string) => `/collaborators/${id}/deactivate`,
    activate: (id: string) => `/collaborators/${id}/activate`,
    hourlyCost: (id: string) => `/collaborators/${id}/hourly-cost`,
    directLeader: (id: string) => `/collaborators/${id}/direct-leader`,
  },
  organizationalUnitLeaders: {
    root: (unitId: string) => `/organizational-units/${unitId}/leaders`,
    byId: (unitId: string, leaderId: string) =>
      `/organizational-units/${unitId}/leaders/${leaderId}`,
  },
  projects: {
    root: '/projects',
    byId: (id: string) => `/projects/${id}`,
    update: (id: string) => `/projects/${id}`,
    responsibleProjectManager: (id: string) => `/projects/${id}/responsible-project-manager`,
    activate: (id: string) => `/projects/${id}/activate`,
    temperature: (id: string) => `/projects/${id}/temperature`,
    temperatureHistory: (id: string) => `/projects/${id}/temperature/history`,
    pause: (id: string) => `/projects/${id}/pause`,
    resume: (id: string) => `/projects/${id}/resume`,
    requestSignature: (id: string) => `/projects/${id}/request-signature`,
    reopen: (id: string) => `/projects/${id}/reopen`,
    complete: (id: string) => `/projects/${id}/complete`,
    audit: (id: string) => `/projects/${id}/audit`,
    archive: (id: string) => `/projects/${id}/archive`,
    approve: (id: string) => `/projects/${id}/approve`,
    reject: (id: string) => `/projects/${id}/reject`,
    pendingAdminApproval: '/projects/pending-admin-approval',
    projectManagers: '/projects/project-managers',
    pendingPortfolioAccess: '/projects/pending-portfolio-access',
    members: (id: string) => `/projects/${id}/members`,
    myAccess: (id: string) => `/projects/${id}/my-access`,
    schedule: (id: string) => `/projects/${id}/schedule`,
    milestones: (id: string) => `/projects/${id}/milestones`,
    statusReports: (id: string) => `/projects/${id}/status-reports`,
    statusReportById: (id: string, reportId: string) =>
      `/projects/${id}/status-reports/${reportId}`,
    discussions: (id: string) => `/projects/${id}/discussions`,
    discussionById: (id: string, discussionId: string) =>
      `/projects/${id}/discussions/${discussionId}`,
    discussionComments: (id: string, discussionId: string) =>
      `/projects/${id}/discussions/${discussionId}/comments`,
    commentById: (id: string, commentId: string) => `/projects/${id}/comments/${commentId}`,
  },
  workItems: {
    root: (projectId: string) => `/projects/${projectId}/work-items`,
    byId: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}`,
    history: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}/history`,
    /** Organização no eixo da hierarquia: pai + vizinhança (PRJ-052-R14). */
    organization: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}/organization`,
    /** Organização no eixo do quadro: coluna + vizinhança (PRJ-052-R14). */
    board: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}/board`,
    /** Organização no eixo das listas nomeadas. */
    taskList: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}/task-list`,
    thread: (projectId: string, workItemId: string) =>
      `/projects/${projectId}/work-items/${workItemId}/threads`,
  },
  tasks: {
    root: (projectId: string) => `/projects/${projectId}/tasks`,
    byId: (projectId: string, taskId: string) => `/projects/${projectId}/tasks/${taskId}`,
    history: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/history`,
    organization: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/organization`,
    subtasks: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks`,
    subtaskById: (projectId: string, taskId: string, subtaskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`,
    subtaskUsers: (projectId: string, taskId: string, subtaskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/users`,
    subtaskCompletion: (projectId: string, taskId: string, subtaskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/complete`,
    ensureThread: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/threads`,
    comments: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/comments`,
    subtaskComments: (projectId: string, taskId: string, subtaskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments`,
    commentById: (projectId: string, taskId: string, commentId: string) =>
      `/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
    moveOnBoard: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/board`,
    moveOnTaskList: (projectId: string, taskId: string) =>
      `/projects/${projectId}/tasks/${taskId}/task-list`,
  },
  threads: {
    byId: (projectId: string, threadId: string) => `/projects/${projectId}/threads/${threadId}`,
    comments: (projectId: string, threadId: string) =>
      `/projects/${projectId}/threads/${threadId}/comments`,
  },
  workBoard: {
    root: (projectId: string) => `/projects/${projectId}/work-board`,
  },
  projectView: {
    root: (projectId: string) => `/projects/${projectId}/project-view`,
  },
  taskLists: {
    root: (projectId: string) => `/projects/${projectId}/task-lists`,
    byId: (projectId: string, taskListId: string) =>
      `/projects/${projectId}/task-lists/${taskListId}`,
  },
  attachments: {
    root: (projectId: string) => `/projects/${projectId}/attachments`,
    byId: (projectId: string, attachmentId: string) =>
      `/projects/${projectId}/attachments/${attachmentId}`,
    download: (projectId: string, attachmentId: string) =>
      `/projects/${projectId}/attachments/${attachmentId}/download`,
    file: (projectId: string, attachmentId: string) =>
      `/projects/${projectId}/attachments/${attachmentId}/file`,
  },
  /** ADR-012: /work-item-sections → /board-sections. */
  workItemSections: {
    root: (projectId: string) => `/projects/${projectId}/board-sections`,
  },
  workItemTypes: {
    root: (projectId: string) => `/projects/${projectId}/work-item-types`,
  },
  taskTypes: {
    root: (projectId: string) => `/projects/${projectId}/task-types`,
  },
  timesheet: {
    project: (projectId: string) => `/projects/${projectId}/timesheet`,
    entries: (projectId: string) => `/projects/${projectId}/time-entries`,
    entryById: (id: string) => `/time-entries/${id}`,
    me: '/timesheet/me',
    audit: '/timesheet/audit',
    /** POST — inicia o timer de um work item do projeto (uma sessão por identidade). */
    timerStart: (projectId: string) => `/projects/${projectId}/timer`,
    /** POST — encerra a sessão e devolve o apontamento criado. */
    timerStop: (timerSessionId: string) => `/timer/${timerSessionId}/stop`,
    /** POST — descarta a sessão sem gerar apontamento. */
    timerDiscard: (timerSessionId: string) => `/timer/${timerSessionId}/discard`,
    /** GET — sessão em andamento da identidade logada (ou `null`). */
    timerCurrent: '/timer/current',
  },
  notifications: {
    root: '/notifications',
    read: (id: string) => `/notifications/${id}/read`,
  },
  settings: {
    global: '/settings',
    statuses: '/settings/statuses',
  },
} as const;
